import { IApiFile, IFile, IFileDataFields, IRawContractorGroupFile, IRawFile } from "@propely/propely-typings";
import { CreatedByData } from "@src/to-be-removed/LocalData/General/CreatedByData";
import { FirebaseData } from "@src/to-be-removed/LocalData/General/FirebaseData";
import { downloadFile } from "@src/utils/hooks/useDownloadFile";
import { GlobalFileData } from "../../../to-be-removed/LocalData/Files/GlobalFileData";
import { FileDocumentData } from "../../../to-be-removed/LocalData/General/FileDocumentData";
import { FirebaseFile } from "../../../to-be-removed/LocalData/General/FirebaseFile";
import { PropertyFileData } from "../../../to-be-removed/LocalData/General/PropertyFileData";
import { FileUtilities } from "../../../utils/fileUtilities";

export type SupportedDisplayedFileData =
  | FileDocumentData
  | FirebaseFile
  | GlobalFileData
  | IApiFile
  | IRawContractorGroupFile
  | IRawFile
  | (IFile & { id: string; propertyID: string; name: string });

/** This is a 'wrapper'-class for the various file document types (currently FileDocumentData
 *  and FirebaseFile) Using this, you don't have to create unique solutions when you are dealing
 *  with different file formats.
 */
export default class DisplayedFileData {
  _file: SupportedDisplayedFileData;

  constructor(file: SupportedDisplayedFileData) {
    this._file = file;
  }

  downloadFile() {
    if ("deleted" in this._file && this._file.deleted) return;
    return downloadFile(this._file, this.propertyID);
  }

  loadFileURL(callback: () => void) {
    if ("externalURL" in this._file && this._file.externalURL) {
      callback();
      return;
    }
    if (this._file instanceof FileDocumentData || this._file instanceof FirebaseFile || this._file instanceof GlobalFileData) {
      this._file.loadFileURL(() => {
        callback();
      });
    }
  }

  loadMetadata(callback: () => void) {
    if (this._file instanceof FileDocumentData || this._file instanceof FirebaseFile || this._file instanceof GlobalFileData) {
      this._file.loadStorageMetadata(() => {
        callback();
      });
    }
  }

  loadData(): Promise<any> {
    if (this._file instanceof FirebaseData) {
      return this._file.load();
    } else {
      return Promise.resolve();
    }
  }

  // Getters
  get id() {
    return this._file.id;
  }
  get path() {
    if (this._file instanceof GlobalFileData) {
      return this._file.storagePath;
    } else if (this._file instanceof FileDocumentData || this._file instanceof FirebaseFile) {
      return this._file.path;
    } else if ("storagePath" in this._file) {
      return this._file.storagePath;
    } else if ("path" in this._file) {
      return this._file.path;
    }
  }
  get previewPath(): string | undefined {
    if (this._file instanceof FileDocumentData || this._file instanceof GlobalFileData) {
      return this._file.previewPath;
    } else if ("previewPath" in this._file && typeof this._file.previewPath === "string") {
      return this._file.previewPath;
    }
    return undefined;
  }

  get name() {
    if (this._file instanceof FileDocumentData || this._file instanceof GlobalFileData) {
      return this._file.name;
    } else if (this._file instanceof FirebaseFile) {
      return this._file.title;
    } else {
      return this._file.name;
    }
  }

  get type(): string {
    if (this._file instanceof FileDocumentData || this._file instanceof GlobalFileData) {
      return this._file.type?.toLowerCase() ?? "";
    } else if (this._file instanceof FirebaseFile) {
      return this._file.fileType.toLowerCase();
    } else {
      return this._file.type?.toLowerCase() ?? "";
    }
  }
  get createdBy(): CreatedByData | undefined {
    if (this._file instanceof FileDocumentData || this._file instanceof FirebaseFile || this._file instanceof GlobalFileData)
      return this._file.createdBy;
    if ("createdBy" in this._file && this._file.createdBy) return new CreatedByData(this._file.createdBy);
    return {};
  }

  get archived(): boolean {
    if ("archived" in this._file && typeof this._file.archived === "boolean") {
      return this._file.archived;
    }
    return false;
  }

  get size(): string {
    return FileUtilities.getFileSizeString(this._file.size);
  }
  get deleted() {
    return "deleted" in this._file && this._file.deleted;
  }
  get fileURL() {
    if ("externalURL" in this._file && this._file.externalURL) {
      return this._file.externalURL;
    }
    if (this._file instanceof FileDocumentData || this._file instanceof FirebaseFile || this._file instanceof GlobalFileData) {
      return this._file.fileURL;
    }
    return undefined;
  }

  get loaded(): boolean {
    if (this._file instanceof FileDocumentData || this._file instanceof FirebaseFile || this._file instanceof GlobalFileData)
      return this._file.loaded;
    return false;
  }

  get isDataLoaded(): boolean {
    if (this._file instanceof FirebaseData) return this._file.loaded;
    else return true;
  }

  get isFindableFile(): boolean {
    if (this._file instanceof PropertyFileData) {
      return this._file.externalFileClientID === "findable";
    } else if ("externalFileClientID" in this._file) {
      return this._file.externalFileClientID === "findable";
    }
    return false;
  }

  get externalFileClientID(): string | undefined {
    if ("externalFileClientID" in this._file && typeof this._file.externalFileClientID === "string") {
      return this._file.externalFileClientID;
    }
    return undefined;
  }

  get externalFileInformation() {
    if (this._file instanceof PropertyFileData) {
      return this._file.externalFileMetadata;
    } else if ("externalFileMetadata" in this._file) {
      return this._file.externalFileMetadata;
    }
    return undefined;
  }

  get propertyID() {
    if (this._file instanceof PropertyFileData) {
      return this._file.parent?.id;
    } else if ("propertyIDs" in this._file && Array.isArray(this._file.propertyIDs) && this._file.propertyIDs.length > 0) {
      return this._file.propertyIDs[0];
    } else {
      const path = this.path;
      const match = path?.match(/\/properties\/([^\/]+)/);
      if (match) {
        return match[1];
      }
      return undefined;
    }
  }

  get externalURL() {
    return "externalURL" in this._file ? this._file.externalURL : undefined;
  }

  get dataFields(): IFileDataFields {
    if (this._file instanceof PropertyFileData || this._file instanceof GlobalFileData) {
      return this._file.dataFields ?? {};
    }

    return "dataFields" in this._file ? (this._file.dataFields ?? {}) : {};
  }
}
